import { InvoiceBody, InvoiceView } from '@lemoncloud/lemon-billings-api';

import { ListResult, Params } from '@codes/base-api';
import { webCore } from '@codes/web-core';

import { SendInvoiceDto, UpdateInvoiceDTO } from '../types';

const BILLING_BACKEND = import.meta.env.VITE_BILLING_ENDPOINT.toLowerCase();

export const createInvoices = async (body: InvoiceBody): Promise<InvoiceView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BILLING_BACKEND}/invoices/0/start-invoice`,
        })
        .setBody({ ...body })
        .execute<InvoiceView>();

    return data;
};

export const makeInvoice = async (body: UpdateInvoiceDTO): Promise<InvoiceView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BILLING_BACKEND}/invoices/${body.invoiceId}/make-invoice`,
        })
        .setBody({ ...body })
        .execute<InvoiceView>();

    return data;
};

export const cancelInvoice = async (body: UpdateInvoiceDTO): Promise<InvoiceView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BILLING_BACKEND}/invoices/${body.invoiceId}/cancel-invoice`,
        })
        .setBody({ ...body })
        .execute<InvoiceView>();

    return data;
};

export const payInvoice = async (body: UpdateInvoiceDTO): Promise<InvoiceView> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${BILLING_BACKEND}/invoices/${body.invoiceId}/pay-invoice`,
        })
        .setBody({ ...body })
        .execute<InvoiceView>();

    return data;
};

export const sendInvoiceEmail = async ({
    body,
    invoiceId,
}: {
    body: SendInvoiceDto;
    invoiceId: string;
}): Promise<InvoiceView> => {
    const { data } = await webCore
        .buildSignedRequest({ method: 'POST', baseURL: `${BILLING_BACKEND}/invoices/${invoiceId}/email` })
        .setParams({ send: '' })
        .setBody(body)
        .execute<InvoiceView>();
    return data;
};

export const fetchInvoices = async (params: Params): Promise<ListResult<InvoiceView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BILLING_BACKEND}/invoices/0/list`,
        })
        .setParams({ ...params })
        .execute<ListResult<InvoiceView>>();

    return { ...data };
};

export const fetchInvoiceById = async (invoiceId?: string): Promise<InvoiceView> => {
    if (!invoiceId) {
        throw new Error('fetchInvoiceById: @invoiceId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${BILLING_BACKEND}/invoices/${invoiceId}/detail`,
        })
        .execute<InvoiceView>();

    return { ...data };
};

export const updateInvoice = async (updateBody: UpdateInvoiceDTO): Promise<InvoiceView> => {
    const { invoiceId, ...data } = updateBody;
    if (!invoiceId) {
        throw new Error('updateInvoice: @invoiceId is required');
    }

    const response = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${BILLING_BACKEND}/invoices/${invoiceId}`,
        })
        .setBody({ ...data })
        .execute<InvoiceView>();

    return response.data;
};

export const deleteInvoice = async (invoiceId: string): Promise<InvoiceView> => {
    if (!invoiceId) {
        throw new Error('deleteInvoice: @invoiceId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${BILLING_BACKEND}/invoices/${invoiceId}`,
        })
        .setBody({})
        .execute<InvoiceView>();

    return data;
};
